import type {
  ContactOrderCreatePayload,
  ContactOrderPlanningPreview,
} from "../../../../../../api/kehrblatt/contact-orders"
import type { CostBenefitEstimate } from "../../../../../../api/kehrblatt/cost-benefit"
import { aggregateContactOrderCostBenefit } from "./costBenefitContributions"

type PreviewResult = {
  previews?: Array<
    ContactOrderPlanningPreview & { index?: number; schedule_event_id?: string | null }
  >
  failed?: Array<{ index?: number; schedule_event_id?: string | null; detail?: string | null }>
  cost_benefit?: CostBenefitEstimate | null
}

type PreviewState = {
  previews: Record<string, ContactOrderPlanningPreview>
  errors: Record<string, string>
}

type InitialRefreshReservation = {
  initialRevision: number
}

type EventRefreshReservation = {
  events: ScheduleEvent[]
  eventRevisions: Map<string, number>
}

export type ContactOrderPreviewControllerOptions = {
  debounceMs?: number
  getTargetEvents: () => ScheduleEvent[]
  buildPayloads: (
    targetEvents: ScheduleEvent[],
  ) => Array<
    ContactOrderCreatePayload & { action_case_id?: string | null; case_id?: string | null }
  >
  loadPreview: (
    payloads: Array<
      ContactOrderCreatePayload & { action_case_id?: string | null; case_id?: string | null }
    >,
  ) => Promise<PreviewResult>
  setPreviewByEventId: (previewByEventId: Record<string, ContactOrderPlanningPreview>) => void
  setErrorsByEventId: (errorsByEventId: Record<string, string>) => void
  setCostBenefit?: (estimate: CostBenefitEstimate | null) => void
  setInitialLoading: (loading: boolean) => void
  setPendingEventIds?: (eventIds: string[]) => void
}

export type ContactOrderPreviewController = {
  mount: () => void
  unmount: () => void
  refresh: () => Promise<void>
  refreshForEvents: (events: ScheduleEvent[]) => Promise<void>
  invalidateForEvents: (events: ScheduleEvent[]) => void
  recomputeCostBenefit: () => void
  queue: (delay?: number) => void
  queueForEvents: (events: ScheduleEvent[], delay?: number) => void
  destroy: () => void
}

const PREVIEW_ERROR_MESSAGE = "Vorschau konnte nicht berechnet werden."

const uniqueEvents = (events: ScheduleEvent[]): ScheduleEvent[] => {
  const eventById = new Map<string, ScheduleEvent>()
  for (const event of events) {
    if (event.id) eventById.set(event.id, event)
  }

  return Array.from(eventById.values())
}

const mapPreviewResult = (result: PreviewResult, targetEvents: ScheduleEvent[]): PreviewState => {
  const eventIdByIndex = targetEvents.map((event) => event.id)
  const previews: Record<string, ContactOrderPlanningPreview> = {}
  for (const preview of result.previews ?? []) {
    const eventId = preview.schedule_event_id || eventIdByIndex[preview.index ?? -1]

    if (eventId) previews[eventId] = preview
  }

  const errors: Record<string, string> = {}
  for (const failed of result.failed ?? []) {
    const eventId = failed.schedule_event_id || eventIdByIndex[failed.index ?? -1]

    if (eventId) errors[eventId] = failed.detail || PREVIEW_ERROR_MESSAGE
  }

  return { previews, errors }
}

export const createContactOrderPreviewController = ({
  debounceMs = 220,
  getTargetEvents,
  buildPayloads,
  loadPreview,
  setPreviewByEventId,
  setErrorsByEventId,
  setCostBenefit = undefined,
  setInitialLoading,
  setPendingEventIds = undefined,
}: ContactOrderPreviewControllerOptions): ContactOrderPreviewController => {
  let initialTimer: ReturnType<typeof setTimeout> | null = null
  let eventTimer: ReturnType<typeof setTimeout> | null = null
  let queuedEvents = new Map<string, ScheduleEvent>()
  let initialRevision = 0
  let eventRevisions = new Map<string, number>()
  let mounted = false
  let destroyed = false
  let previewByEventId: Record<string, ContactOrderPlanningPreview> = {}
  let errorsByEventId: Record<string, string> = {}
  let costBenefitMetadata: CostBenefitEstimate | null = null
  const pendingEventIds = new Set<string>()

  const recomputeCostBenefit = () => {
    if (destroyed) return

    setCostBenefit?.(
      aggregateContactOrderCostBenefit({
        activeEventIds: uniqueEvents(getTargetEvents()).map((event) => event.id),
        previewByEventId,
        errorsByEventId,
        metadata: costBenefitMetadata,
      }),
    )
  }

  const publishPreviewState = () => {
    setPreviewByEventId(previewByEventId)
    setErrorsByEventId(errorsByEventId)
    recomputeCostBenefit()
  }

  const publishPendingEventIds = () => {
    setPendingEventIds?.(Array.from(pendingEventIds))
  }

  const reserveInitialRefresh = (): InitialRefreshReservation => {
    const reservation = { initialRevision: ++initialRevision }
    setInitialLoading(true)

    return reservation
  }

  const reserveEventRefresh = (events: ScheduleEvent[]): EventRefreshReservation | null => {
    const targetEvents = uniqueEvents(events)

    if (!targetEvents.length) return null

    const revisions = new Map<string, number>()
    for (const event of targetEvents) {
      const revision = (eventRevisions.get(event.id) ?? 0) + 1
      eventRevisions.set(event.id, revision)
      revisions.set(event.id, revision)
      pendingEventIds.add(event.id)
    }
    publishPendingEventIds()

    return { events: targetEvents, eventRevisions: revisions }
  }

  const currentEventIds = (revisions: Map<string, number>) =>
    Array.from(revisions.entries())
      .filter(([eventId, revision]) => eventRevisions.get(eventId) === revision)
      .map(([eventId]) => eventId)

  const applyScopedPreviewState = (state: PreviewState, revisions: Map<string, number>) => {
    if (destroyed) return false

    const eventIds = currentEventIds(revisions)

    if (!eventIds.length) return false

    const nextPreviews = { ...previewByEventId }
    const nextErrors = { ...errorsByEventId }
    for (const eventId of eventIds) {
      delete nextPreviews[eventId]
      delete nextErrors[eventId]

      if (state.previews[eventId]) nextPreviews[eventId] = state.previews[eventId]

      if (state.errors[eventId]) nextErrors[eventId] = state.errors[eventId]
    }
    previewByEventId = nextPreviews
    errorsByEventId = nextErrors
    publishPreviewState()

    return true
  }

  const applyInitialPreviewState = (
    state: PreviewState,
    capturedEventRevisions: Map<string, number>,
    revision: number,
  ) => {
    if (destroyed || revision !== initialRevision) return false

    const nextPreviews = { ...state.previews }
    const nextErrors = { ...state.errors }
    for (const [eventId, capturedRevision] of capturedEventRevisions) {
      if ((eventRevisions.get(eventId) ?? 0) === capturedRevision) continue

      delete nextPreviews[eventId]
      delete nextErrors[eventId]

      if (previewByEventId[eventId]) nextPreviews[eventId] = previewByEventId[eventId]

      if (errorsByEventId[eventId]) nextErrors[eventId] = errorsByEventId[eventId]
    }
    previewByEventId = nextPreviews
    errorsByEventId = nextErrors
    publishPreviewState()

    return true
  }

  const finishEventRefresh = (revisions: Map<string, number>) => {
    if (destroyed) return

    let changed = false
    for (const [eventId, revision] of revisions) {
      if (eventRevisions.get(eventId) !== revision) continue

      changed = pendingEventIds.delete(eventId) || changed
    }

    if (changed) publishPendingEventIds()
  }

  const executeInitialRefresh = async ({
    initialRevision: requestRevision,
  }: InitialRefreshReservation) => {
    if (destroyed || requestRevision !== initialRevision) return

    const targetEvents = uniqueEvents(getTargetEvents())
    const capturedEventRevisions = new Map(
      targetEvents.map((event) => [event.id, eventRevisions.get(event.id) ?? 0] as const),
    )

    if (!targetEvents.length) {
      costBenefitMetadata = null
      applyInitialPreviewState(
        { previews: {}, errors: {} },
        capturedEventRevisions,
        requestRevision,
      )

      if (!destroyed && requestRevision === initialRevision) setInitialLoading(false)

      return
    }

    try {
      const result = await loadPreview(buildPayloads(targetEvents))

      if (!destroyed && requestRevision === initialRevision) {
        costBenefitMetadata = result.cost_benefit ?? null
      }

      applyInitialPreviewState(
        mapPreviewResult(result, targetEvents),
        capturedEventRevisions,
        requestRevision,
      )
    } catch (error) {
      const message = error instanceof Error ? error.message : PREVIEW_ERROR_MESSAGE
      costBenefitMetadata = null
      applyInitialPreviewState(
        {
          previews: {},
          errors: Object.fromEntries(targetEvents.map((event) => [event.id, message])),
        },
        capturedEventRevisions,
        requestRevision,
      )

      if (!destroyed && requestRevision === initialRevision) setCostBenefit?.(null)
    } finally {
      if (!destroyed && requestRevision === initialRevision) setInitialLoading(false)
    }
  }

  const executeEventRefresh = async ({
    events,
    eventRevisions: requestEventRevisions,
  }: EventRefreshReservation) => {
    if (destroyed) return

    const targetEvents = uniqueEvents(events)

    if (!targetEvents.length) {
      applyScopedPreviewState({ previews: {}, errors: {} }, requestEventRevisions)
      finishEventRefresh(requestEventRevisions)

      return
    }

    try {
      const result = await loadPreview(buildPayloads(targetEvents))

      if (costBenefitMetadata === null && result.cost_benefit) {
        costBenefitMetadata = result.cost_benefit
      }

      applyScopedPreviewState(mapPreviewResult(result, targetEvents), requestEventRevisions)
    } catch (error) {
      const message = error instanceof Error ? error.message : PREVIEW_ERROR_MESSAGE
      applyScopedPreviewState(
        {
          previews: {},
          errors: Object.fromEntries(events.map((event) => [event.id, message])),
        },
        requestEventRevisions,
      )
    } finally {
      finishEventRefresh(requestEventRevisions)
    }
  }

  const refresh = async () => {
    await executeInitialRefresh(reserveInitialRefresh())
  }

  const refreshForEvents = async (events: ScheduleEvent[]) => {
    const reservation = reserveEventRefresh(events)

    if (reservation) await executeEventRefresh(reservation)
  }

  // Registry edits invalidate old channel availability as well as in-flight preview responses.
  const invalidateForEvents = (events: ScheduleEvent[]) => {
    if (destroyed) return

    const reservation = reserveEventRefresh(events)

    if (!reservation) return

    for (const event of reservation.events) queuedEvents.delete(event.id)
    applyScopedPreviewState({ previews: {}, errors: {} }, reservation.eventRevisions)
    finishEventRefresh(reservation.eventRevisions)
  }

  const queue = (delay = debounceMs) => {
    if (!mounted || destroyed) return

    if (initialTimer) clearTimeout(initialTimer)

    const reservation = reserveInitialRefresh()
    initialTimer = setTimeout(() => {
      initialTimer = null
      void executeInitialRefresh(reservation)
    }, delay)
  }

  const queueForEvents = (events: ScheduleEvent[], delay = debounceMs) => {
    if (!mounted || destroyed) return

    const reservation = reserveEventRefresh(events)

    if (!reservation) return

    for (const event of reservation.events) queuedEvents.set(event.id, event)

    if (eventTimer) clearTimeout(eventTimer)

    eventTimer = setTimeout(() => {
      eventTimer = null
      const targetEvents = Array.from(queuedEvents.values())
      queuedEvents = new Map()
      void executeEventRefresh({
        events: targetEvents,
        eventRevisions: new Map(
          targetEvents.map((event) => [event.id, eventRevisions.get(event.id) ?? 0] as const),
        ),
      })
    }, delay)
  }

  const mount = () => {
    if (destroyed) return

    mounted = true
    queue(0)
  }

  const unmount = () => {
    mounted = false
  }

  const destroy = () => {
    if (initialTimer) clearTimeout(initialTimer)

    if (eventTimer) clearTimeout(eventTimer)

    initialTimer = null
    eventTimer = null
    queuedEvents = new Map()
    initialRevision += 1
    eventRevisions = new Map()
    mounted = false
    destroyed = true
    pendingEventIds.clear()
    setInitialLoading(false)
    publishPendingEventIds()
  }

  return {
    mount,
    unmount,
    refresh,
    refreshForEvents,
    invalidateForEvents,
    recomputeCostBenefit,
    queue,
    queueForEvents,
    destroy,
  }
}
